import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "gatsby";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";
import { MobileNavigationEvents } from "../global/MobileNavigationEvents";

import blockImg from "../../images/block-green.svg";
import networkImg from "../../images/network-pink.svg";
import stablecoinsImg from "../../images/stablecoin-purple.svg";

export const Value = (props) => {
  const data = props.data || {};
  // Expecting data.items = [{ url, title, label, description }] from WP
  const items = Array.isArray(data.items) ? data.items : [];

  const [isMobile, setIsMobile] = useState(false);
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [totalSlides, setTotalSlides] = useState(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const checkMobile = () => setIsMobile(window.innerWidth <= 768);
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }
  }, []);

  const onSplideMounted = (splide) => setTotalSlides(splide.length);
  const handlePrev = () => sliderRef.current?.splide?.go("-1");
  const handleNext = () => sliderRef.current?.splide?.go("+1");

  // Local visuals (images/colors/sizing) — keep as-is
  const VISUALS = [
    { imgSrc: blockImg,   bgClass: "bg-green",  imgWidth: 117, fallbackUrl: "/use-cases/" },
    { imgSrc: networkImg, bgClass: "bg-pink",   imgWidth: 130, fallbackUrl: "/ecosystem/" },
    { imgSrc: stablecoinsImg, bgClass: "bg-purple", imgWidth: 100, fallbackUrl: "/technology/" },
  ];

  // Merge WP text into the visuals by index.
  // Text fields are WP-only (no marketing defaults): empty if WP doesn't provide them.
  const slides = VISUALS.map((v, i) => {
    const it = items[i] || {};
    return {
      url: it.url || v.fallbackUrl, // keep URLs working even if WP omitted them
      title: it.title || "",        // e.g. "The home of |DeFi on Bitcoin"
      label: it.label || "",        // e.g. "01"
      description: it.description ?? it.text ?? "", // support either "description" or legacy "text"
      imgSrc: v.imgSrc,
      bgClass: v.bgClass,
      imgWidth: v.imgWidth,
    };
  });

  // If you want to hide a card when WP text is missing, you can filter here:
  // const slides = VISUALS.map(...).filter(s => s.title || s.description);

  const TitleLines = ({ title, bgClass }) => {
    if (!title) return null;
    return (
      <>
        {title.split("|").map((line, i) => (
          <span
            key={i}
            className={`${bgClass} zg-line-height-sm letter-spacing-1 p-2 mb-1`}
          >
            {line.trim()}
          </span>
        ))}
      </>
    );
  };

  return (
    <div className="d-flex justify-center align-center pb-lg-16 mt-lg-16 mt-5 pb-11" id="value">
      <div className="container">
        {isMobile ? (
          <>
            <Splide
              ref={sliderRef}
              options={{ type: "loop", perPage: 1, perMove: 1, gap: "1rem", pagination: false, arrows: false }}
              onMove={(_, newIndex) => setCurrentSlide(newIndex + 1)}
              onMounted={onSplideMounted}
            >
              {slides.map((slide, index) => (
                <SplideSlide key={index}>
                  <Link to={slide.url} className="text-decoration-none">
                    <div className="zg-card d-flex flex-column value-prop justify-content-between text-white align-items-start border hover-card rounded-4">
                      <div>
                        <h3 className="h4 text-black mb-5 d-flex align-items-start">
                          <span className="d-flex flex-column align-items-start me-2">
                            <TitleLines title={slide.title} bgClass={slide.bgClass} />
                          </span>
                          <div className="d-flex align-items-end">
                            {slide.label ? (
                              <span className={`d-inline-block ${slide.bgClass} px-2 py-1 zg-label`}>
                                {slide.label}
                              </span>
                            ) : null}
                          </div>
                        </h3>
                        <div className="zg-text-md">
                          {slide.description ? <p>{slide.description}</p> : null}
                        </div>
                      </div>
                      <div className="d-flex justify-content-end full-width hidden-mobile">
                        <img
                          src={slide.imgSrc}
                          alt="slide illustration"
                          width={slide.imgWidth}
                          height="auto"
                          loading="lazy"
                          className="hidden-mobile"
                        />
                      </div>
                    </div>
                  </Link>
                </SplideSlide>
              ))}
            </Splide>
            <MobileNavigationEvents
              currentSlide={currentSlide}
              totalSlides={totalSlides}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          </>
        ) : (
          <div className="zg-items-grid">
            {slides.map((slide, index) => (
              <Link to={slide.url} key={index} className="text-decoration-none text-white">
                <div className="zg-card d-flex flex-column justify-content-between align-items-start p-4 border rounded-4 hover-card">
                  <div>
                    <h3 className="h4 text-black mb-5 d-flex">
                      <span className="d-flex flex-column align-items-start me-2">
                        <TitleLines title={slide.title} bgClass={slide.bgClass} />
                      </span>
                      <div className="d-flex align-items-end value-tag">
                        {slide.label ? (
                          <span className={`d-inline-block ${slide.bgClass} px-2 py-1 zg-label`}>
                            {slide.label}
                          </span>
                        ) : null}
                      </div>
                    </h3>
                    <div className="zg-text-md">
                      {slide.description ? <p>{slide.description}</p> : null}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end full-width">
                    <img
                      src={slide.imgSrc}
                      alt="slide illustration"
                      width={slide.imgWidth}
                      height="auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

Value.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,       // supports "|" for line breaks
        label: PropTypes.string,       // "01", "02", "03"
        description: PropTypes.string, // or use "text" field in ACF and it will still work
        text: PropTypes.string,        // legacy compatibility
      })
    ),
  }),
};
